import { useQuery } from "@tanstack/react-query";
import { fetchPublicReport } from "../lib/api";

interface UseReportStatusOptions {
  initialStatus?: string;
  publicToken: string;
  enabled?: boolean;
}

/**
 * Hook to poll for report status updates until the report is complete.
 * This enables real-time UI updates when a report finishes generating.
 * 
 * @param options - Configuration options
 * @returns Current report status and loading state
 */
export function useReportStatus({ 
  initialStatus, 
  publicToken,
  enabled = true 
}: UseReportStatusOptions) {
  const { data: reportData, isLoading } = useQuery({
    queryKey: ["ai-report-status", publicToken],
    queryFn: async () => {
      return await fetchPublicReport(publicToken);
    },
    enabled: enabled && Boolean(publicToken),
    refetchInterval: (query) => {
      const data = query.state.data as any;
      const status = data?.report?.status;
      // Poll every 3 seconds while report is not complete
      // Stops polling once status is "complete" or "failed"
      return (status && status !== "complete" && status !== "failed") ? 3000 : false;
    },
    // Use initial status from prop for immediate render
    initialData: initialStatus ? { report: { status: initialStatus } } : undefined,
  });

  const status = reportData?.report?.status || initialStatus;
  const isComplete = status === "complete";

  return {
    status,
    isComplete,
    isLoading,
  };
}


